import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  Checkbox,
  CircularProgress,
  Paper,
  Divider,
  IconButton
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useFramework } from '../../contexts/FrameworkContext';

/**
 * Sidebar component for displaying and selecting frameworks.
 */
const FrameworkSidebar = ({ onStepClick }) => {
  const { 
    frameworks, 
    activeFramework, 
    loading, 
    selectFramework, 
    toggleStepCompletion 
  } = useFramework();
  
  const [showFrameworks, setShowFrameworks] = useState(true);

  // Show loading indicator when fetching data
  if (loading && !activeFramework && frameworks.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper 
      elevation={0} 
      sx={{
        borderRight: '1px solid rgba(0, 0, 0, 0.12)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Frameworks section header with collapsible control */}
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: activeFramework ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Frameworks
        </Typography>
        <IconButton 
          size="small" 
          onClick={() => setShowFrameworks(!showFrameworks)}
          sx={{ ml: 1 }}
        >
          {showFrameworks && !activeFramework ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      
      {/* Scrollable content area */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {/* Frameworks list - collapsible when a framework is active */}
        <Collapse in={showFrameworks || !activeFramework}>
          <Box sx={{ p: 2 }}>
            <List disablePadding>
              {frameworks.map((framework) => (
                <ListItem key={framework.id} disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton 
                    onClick={() => {
                      selectFramework(framework.id);
                      // Auto-collapse the frameworks list when selecting one
                      setShowFrameworks(false);
                    }}
                    selected={activeFramework?.id === framework.id}
                    sx={{ 
                      borderRadius: 1,
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(0, 0, 0, 0.06)',
                      }
                    }}
                  >
                    <ListItemText 
                      primary={framework.title} 
                      secondary={framework.category}
                      primaryTypographyProps={{
                        variant: 'body2',
                        fontWeight: activeFramework?.id === framework.id ? 600 : 400
                      }}
                      secondaryTypographyProps={{
                        variant: 'caption'
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Collapse>

        {/* Active framework steps section */}
        {activeFramework && (
          <Box sx={{ mt: !showFrameworks ? 0 : 2 }}>
            {showFrameworks && <Divider />}
            <Box 
              sx={{ 
                p: 2,
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                {activeFramework.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {activeFramework.overview}
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Steps:
              </Typography>
              <List disablePadding>
                {activeFramework.steps.map((step) => (
                  <StepItem 
                    key={step.id} 
                    step={step} 
                    onStepClick={onStepClick}
                    onToggleCompletion={toggleStepCompletion}
                  />
                ))}
              </List>
            </Box>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

/**
 * Individual step item with toggle and details.
 */
const StepItem = ({ step, onStepClick, onToggleCompletion }) => {
  const [open, setOpen] = useState(false);
  
  /* eslint-disable no-unused-vars */
  const handleToggleDetails = (e) => {
    e.stopPropagation();
    setOpen(!open);
  };
  /* eslint-enable no-unused-vars */

  const handleToggleCompletion = (e) => {
    e.stopPropagation();
    onToggleCompletion(step.id, !step.completed);
  };

  const handleStepClick = () => {
    onStepClick(step);
  };

  return (
    <>
      <ListItem
        disablePadding
        secondaryAction={
          <Checkbox
            edge="end"
            checked={step.completed || false}
            onChange={handleToggleCompletion}
            inputProps={{ 'aria-labelledby': `step-${step.id}` }}
          />
        }
        sx={{ mb: 0.5 }}
      >
        <ListItemButton 
          onClick={handleStepClick}
          sx={{ 
            pr: 6,
            borderRadius: 1,
            backgroundColor: step.completed ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
          }}
        >
          <ListItemText
            id={`step-${step.id}`}
            primary={
              <Typography
                variant="body2"
                sx={{
                  textDecoration: step.completed ? 'line-through' : 'none',
                  fontWeight: 500,
                  opacity: step.completed ? 0.6 : 1,
                }}
              >
                {step.number}. {step.title}
              </Typography>
            }
          />
        </ListItemButton>
      </ListItem>
    </>
  );
};

export default FrameworkSidebar;